import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      toast.success(`Welcome back, ${result.user.name}!`);
      switch (result.user.role) {
        case 'admin': navigate('/admin'); break;
        case 'staff': navigate('/staff'); break;
        case 'student': navigate('/student'); break;
        default: navigate('/');
      }
    } else {
      setError(result.error);
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_signature-psc-portal/artifacts/h1jchf2q_IMG-20260126-WA0188.jpg" alt="Signature PSC" className="w-16 h-16 rounded-lg object-contain bg-white p-1 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Welcome Back
          </h1>
          <p className="text-sm text-[#a1a1aa] mt-1">Sign in to your account</p>
          <p className="text-[10px] text-[#52525b] mt-1">A unit of <span className="text-[#d4af37]">Narzis Ventures LLP</span></p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 rounded-lg bg-[#18181b] border border-[#27272a] space-y-5" data-testid="login-form">
          {error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <div className="space-y-2">
            <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider">Username / Email</Label>
            <Input type="text" value={email} onChange={e => setEmail(e.target.value)} required
              placeholder="Enter username or email"
              className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37]"
              data-testid="login-email-input" />
          </div>

          <div className="space-y-2">
            <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider">Password</Label>
            <div className="relative">
              <Input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                placeholder="Enter your password"
                className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] pr-10"
                data-testid="login-password-input" />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525b] hover:text-[#a1a1aa]"
                data-testid="toggle-password-btn">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading}
            className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold py-3 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:shadow-[0_0_25px_rgba(212,175,55,0.5)]"
            data-testid="login-submit-btn">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          <p className="text-center text-xs text-[#52525b] mt-4">
            Students: Use credentials provided by your centre.
          </p>
        </form>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-[#a1a1aa] hover:text-[#d4af37] transition-colors" data-testid="back-home-link">
            &larr; Back to Home
          </a>
        </div>
      </motion.div>
    </div>
  );
}
